<script setup>
    import {
        ref,
        onMounted,
        onUnmounted,
        computed,
        toRefs,
        watch,
        shallowRef,
    } from 'vue';

    import { useBookingStore } from '@/stores/booking';
    import {
        dayFromDate,
        convertTo12Hour,
        getMonthYear,
    } from '@/helpers/dateFormats';

    import '@/assets/css/carousel.css';
    import { Carousel, Slide } from 'vue3-carousel';
    import { getErrorMessage } from '~/helpers/commonUtils';
    import Spinner from './common/Spinner.vue';

    const props = defineProps({
        availability: {
            type: Object,
            default: () => ({}),
        },
        isSubmitted: Boolean,
    });

    const { availability, isSubmitted } = toRefs(props);

    const bookingStore = useBookingStore();

    /* ------------------ STATE ------------------ */
    const availabilityValue = computed(() =>
        Object.keys(availability.value || {})
    );

    const dates = availabilityValue;

    const currentlySelectedDate = ref(bookingStore.state?.selected_date);
    const currentlySelectedTime = ref(bookingStore.state?.start_time);
    const isLoader = ref(false);

    const dateCarousel = shallowRef(null);
    const screenWidth = ref(window.innerWidth);
    const currentSlide = ref(0);

    /* ------------------ UI COMPUTED ------------------ */
    const visibleSlides = computed(() => {
        if (screenWidth.value >= 1280) return 7;
        if (screenWidth.value >= 1024) return 4;
        return 3;
    });

    const maxSlide = computed(() =>
        Math.max(dates.value.length - visibleSlides.value, 0)
    );

    const isPrevDisabled = computed(() => currentSlide.value === 0);
    const isNextDisabled = computed(() => currentSlide.value >= maxSlide.value);

    /* ------------------ TIMES ------------------ */
    const dynamicTimes = computed(() => {
        if (!currentlySelectedDate.value) return [];

        const selected = availability.value[currentlySelectedDate.value];

        if (!Array.isArray(selected)) return [];

        return selected.map((t) => ({
            ...t,
            start_display: convertTo12Hour(t.start),
            end_display: convertTo12Hour(t.end),
        }));
    });

    /* ------------------ INITIAL DATE ------------------ */
    watch(
        availabilityValue,
        (keys) => {
            if (!keys.length) return;

            // preserve existing valid selection
            const currentDate = bookingStore.state.selected_date;
            if (keys.includes(currentDate)) return;

            const firstDate = keys[0];

            currentlySelectedDate.value = firstDate;

            bookingStore.updateBookingState({
                selected_date: firstDate,
                date: firstDate,
                slide_index: 0,
            });
        },
        { immediate: true }
    );

    /* ------------------ RESET TIME ------------------ */
     watch(
        () => bookingStore.state.start_time,
        (newVal) => {
            if (newVal === '') {
                currentlySelectedTime.value = ''
            }
        },
        { immediate: true }
    );

    /* ------------------ LOADER CONTROL ------------------ */
    watch(
        [() => bookingStore.state.is_loader, dynamicTimes],
        async ([pageLoader, times]) => {
            // start loader
            if (pageLoader) {
                isLoader.value = true;
                return;
            }

            // stop loader when the request has settled (data ready or failed)
            await nextTick();
            isLoader.value = false;
        },
    );

    /* ------------------ EVENTS ------------------ */
    const selectDate = (key, index) => {
        currentlySelectedDate.value = key;
        currentlySelectedTime.value = '';

        bookingStore.updateBookingState({
            selected_date: key,
            date: key,
            slide_index: index,
            start_time: '',
            end_time: '',
        });
    };

    const selectTime = (item) => {
        currentlySelectedTime.value = item.start;

        bookingStore.updateBookingState({
            start_time: item.start,
            end_time: item.end,
        });
    };

    const scrollRight = () => dateCarousel.value?.next();
    const scrollLeft = () => dateCarousel.value?.prev();

    const onSlideEnd = (data) => {
        const i =
            data?.currentSlide ??
            data?.currentSlideIndex ??
            data?.slidingToIndex;

        if (typeof i === 'number') currentSlide.value = i;
    };

    /* ------------------ RESIZE ------------------ */
    const handleResize = () => {
        screenWidth.value = window.innerWidth;
    };

    onMounted(() => {
        window.addEventListener('resize', handleResize);
    });

    onUnmounted(() => {
        window.removeEventListener('resize', handleResize);
    });
</script>

<template>
    <div
        v-if="isLoader"
        class="flex flex-col items-center space-y-2">
        <Spinner :show="isLoader" />
        <span class="text-center text-[#CC3D40]">
            Please wait a moment. We are checking available date and time.
        </span>
    </div>

    <div v-if="availabilityValue.length && !isLoader">
        <!-- SELECT DATE BLOCK -->
        <div class="mb-5 md:mb-8">
            <!-- HEADING -->
            <div class="mb-4 mt-5 md:mt-8">
                <p class="font-semibold text-base">
                    Select a date
                </p>
            </div>

            <!-- ARROW BUTTON FOR DATE SELECTION -->
            <div class="flex flex-row justify-between items-center my-4">
                <!-- LEFT ARROW BUTTON -->
                <div>
                    <button
                        :disabled="isPrevDisabled"
                        :class="[
                            'bg-black/5 p-3 rounded-full',
                            isPrevDisabled && 'opacity-30 cursor-not-allowed',
                        ]"
                        @click="scrollLeft">
                        <Icon
                            class="!w-[24px] pointer-events-none !h-[24px] block mx-auto"
                            icon="arrow-left-alt"
                            aria-label="arrow left icon"
                            alt="U.P Location Icon" />
                    </button>
                </div>
                <!-- MONTH DISPLAY -->
                <div class="text-xl font-bold uppercase">
                    {{
                        currentlySelectedDate
                            ? getMonthYear(currentlySelectedDate)
                            : ''
                    }}
                </div>
                <!-- RIGHT ARROW BUTTON -->
                <div>
                    <button
                        :disabled="isNextDisabled"
                        :class="[
                            'bg-black/5 p-3 rounded-full',
                            isNextDisabled && 'opacity-30 cursor-not-allowed',
                        ]"
                        @click="scrollRight">
                        <Icon
                            class="!w-[24px] pointer-events-none !h-[24px] block mx-auto"
                            icon="arrow-right-alt"
                            aria-label="arrow right icon"
                            alt="U.P Location Icon" />
                    </button>
                </div>
            </div>

            <!-- DATE SELECTOR -->
            <Carousel
                ref="dateCarousel"
                :wrapAround="false"
                :snapAlign="'start'"
                @slide-end="onSlideEnd"
                :breakpoints="{
                    320: { itemsToShow: 3 },
                    1024: { itemsToShow: 4 },
                    1280: { itemsToShow: 7 },
                }">
                <Slide
                    v-for="(key, index) in dates"
                    :key="key">
                    <button
                        @click="selectDate(key, index)"
                        class="border rounded-xl border-[#A0A2A4] flex flex-col items-center justify-center py-2 px-4 text-center mx-1 w-full"
                        :class="{
                            'bg-[#161412] text-white border-[#ffffff]':
                                currentlySelectedDate === key,
                        }">
                        <div>
                            <p class="uppercase text-xs">
                                {{ dayFromDate(key, false, true) }}
                            </p>
                            <p class="text-lg font-semibold">
                                {{ dayFromDate(key, false, false) }}
                            </p>
                        </div>
                    </button>
                </Slide>
            </Carousel>
            <!-- Error -->
            <p
                v-if="isSubmitted && !currentlySelectedDate"
                class="text-[#CC3D40] text-sm mt-1 err-sub-text">
                {{ getErrorMessage('date') }}
            </p>
        </div>

        <!-- SELECT TIME BLOCK -->
        <div>
            <!-- HEADING -->
            <div class="mb-4 mt-5 md:mt-8">
                <p class="font-semibold text-base">
                    Choose a time
                </p>
                <p class="text-[#888B8D] leading-4 text-sm mb-3">
                    30 minute duration
                </p>
            </div>

            <!-- TIME SELECTOR -->
            <ul
                v-if="dynamicTimes.length"
                class="grid grid-cols-3 md:grid-cols-5 gap-2 mt-4">
                <li
                    v-for="(item, key, index) in dynamicTimes"
                    :key="index"
                    :class="['cursor-pointer']"
                    @click="selectTime(item)">
                    <div
                        class="border rounded-full border-[#A0A2A4] flex flex-col items-center justify-center p-[6px] px-2 text-center"
                        :class="{
                            'bg-[#161412] text-white border-[#ffffff]':
                                currentlySelectedTime === item.start,
                        }">
                        <p class="uppercase">{{ item.start_display }}</p>
                    </div>
                </li>
            </ul>
            <p
                v-else
                class="text-center text-[#717171] mt-4">
                {{
                    currentlySelectedDate
                        ? 'No available time slots for the selected date. Please choose a different date or check back later.'
                        : 'Please choose an appropriate date first.'
                }}
            </p>
            <!-- Error -->
            <p
                v-if="isSubmitted && !currentlySelectedTime"
                class="text-[#CC3D40] text-sm mt-1 err-sub-text">
                {{ getErrorMessage('time') }}
            </p>
        </div>
    </div>

    <div v-if="!availabilityValue.length && !isLoader">
        <p class="text-center text-[#717171]">
            No available slots for the selected location. Please choose a
            different location or check back later.
        </p>
    </div>
</template>

<style scoped>
    .heading-icon {
        width: 20px;
        height: 20px;
        margin-right: 8px;
    }
</style>
